import { Application, Assets, Container, Graphics, Sprite, Texture } from 'pixi.js';
import { el, clear, toast, notifyOnce } from '../dom';
import type { GameCtx, ViewTeardown } from '../types';
import { attachCamera, type Camera } from '../map/camera';
import { createPlayer, FOOT_OFFSET, type PlayerHandle } from '../map/player';
import { loadAppearance } from '../character/appearance-store';
import { defaultAppearance } from '../character/compositor';
import { TILE } from '../map/world';
import { mulberry32 } from '../map/prng';
import { createFog, type Fog } from '../map/fog';
import { dungeonApi, fieldApi, type DungeonConfig } from '../game-api';
import { dispatchAction, isDropped } from '../action/dispatch';
import { richText } from '../ui/icons';
import { setAudioOverlay } from '../audio/audio';

/**
 * The dungeon (Block 6). A dark, torch-lit cave reached by standing on an
 * overworld entrance. It is its own scene — a cellular-automata cave with
 * impassable rock, ore veins whose tier rises with DEPTH, skeletons that spawn
 * around the clock, and stairs that descend three levels (each darker, gem-richer
 * and tougher) or climb back to the surface. Loot + the mob-HP scaling are
 * server-owned (dungeonApi); the rest is client-rendered like the farm.
 */

const COLS = 48, ROWS = 40;          // cave size in tiles
const CONTACT = TILE * 0.8;          // skeleton melee range
const ATTACK_REACH = TILE * 1.6;     // player can strike / mine within this
// 4-way facing toward a world point, so the hero swings AT the skeleton/vein
// instead of whichever way he last walked.
const faceDir = (dx: number, dy: number): 'up' | 'down' | 'left' | 'right' =>
  (Math.abs(dx) >= Math.abs(dy) ? (dx < 0 ? 'left' : 'right') : (dy < 0 ? 'up' : 'down'));
const DMG_COOLDOWN_MS = 1200;

/**
 * Is the target close enough to hit? Says "too far" when it is not — and, when it
 * is, CLEARS the memory so the refusal can be said again later.
 *
 * The clear is the whole point. `notifyOnce` latches a key until it is passed
 * `null`, so without this the warning was shown exactly once per session: the
 * second time the player mashed attack from across the room he got the silent
 * no-op the message exists to prevent. Getting in range is the "situation
 * resolved" event, the same way mounting clears the horse key in farm/input.ts.
 */
export function withinAttackReach(dist: number, reach: number = ATTACK_REACH): boolean {
  if (dist > reach) { notifyOnce('reach', 'Too far — get closer'); return false; }
  notifyOnce('reach', null);
  return true;
}

type Cave = { wall: Uint8Array; floors: number[]; spawn: [number, number]; down: [number, number]; up: [number, number] };
interface Skeleton { id: string; sprite: Sprite; x: number; y: number; hp: number; hurtCd: number; dying: boolean; }
interface Vein { sprite: Sprite; tx: number; ty: number; gem: boolean; hits: number; }

const idx = (x: number, y: number) => y * COLS + x;
const MOVE_CODES = new Set(['KeyW', 'KeyA', 'KeyS', 'KeyD', 'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight']);

/** Cellular-automata cave: 1 = rock wall, 0 = floor. Largest region only. */
function genCave(seed: number): Cave {
  const rnd = mulberry32(seed);
  let wall = new Uint8Array(COLS * ROWS);
  for (let y = 0; y < ROWS; y++)
    for (let x = 0; x < COLS; x++)
      wall[idx(x, y)] = (x < 2 || y < 2 || x >= COLS - 2 || y >= ROWS - 2 || rnd() < 0.45) ? 1 : 0;
  for (let it = 0; it < 4; it++) {
    const next = new Uint8Array(COLS * ROWS);
    for (let y = 0; y < ROWS; y++)
      for (let x = 0; x < COLS; x++) {
        let n = 0;
        for (let dy = -1; dy <= 1; dy++)
          for (let dx = -1; dx <= 1; dx++) {
            if (!dx && !dy) continue;
            const nx = x + dx, ny = y + dy;
            if (nx < 0 || ny < 0 || nx >= COLS || ny >= ROWS || wall[idx(nx, ny)]) n++;
          }
        next[idx(x, y)] = n >= 5 ? 1 : 0;
      }
    wall = next;
  }
  // Keep only the largest connected floor region; wall off the rest.
  const seen = new Uint8Array(COLS * ROWS);
  let best: number[] = [];
  for (let i = 0; i < wall.length; i++) {
    if (wall[i] || seen[i]) continue;
    const region: number[] = [], q = [i]; seen[i] = 1;
    for (let h = 0; h < q.length; h++) {
      const c = q[h], x = c % COLS, y = (c / COLS) | 0; region.push(c);
      for (const [nx, ny] of [[x - 1, y], [x + 1, y], [x, y - 1], [x, y + 1]] as const) {
        if (nx < 0 || ny < 0 || nx >= COLS || ny >= ROWS) continue;
        const j = idx(nx, ny);
        if (!wall[j] && !seen[j]) { seen[j] = 1; q.push(j); }
      }
    }
    if (region.length > best.length) best = region;
  }
  const keep = new Set(best);
  for (let i = 0; i < wall.length; i++) if (!wall[i] && !keep.has(i)) wall[i] = 1;
  const floors = best;
  // Spawn near one end, stairs-down at the farthest floor tile from spawn.
  const spawn = floors[Math.floor(rnd() * floors.length)];
  const sx = spawn % COLS, sy = (spawn / COLS) | 0;
  let far = spawn, farD = -1;
  for (const f of floors) {
    const d = Math.hypot((f % COLS) - sx, ((f / COLS) | 0) - sy);
    if (d > farD) { farD = d; far = f; }
  }
  return {
    wall, floors,
    spawn: [sx, sy],
    down: [far % COLS, (far / COLS) | 0],
    up: [sx, sy],   // you climb out where you came in
  };
}

/** Preloaded in renderDungeon so Texture.from() below finds it in the cache. */
const SKELETON_SPRITE = '/sprites/mobs/skeleton_idle_down_0.png';

export function renderDungeon(
  root: HTMLElement,
  ctx: GameCtx,
  entranceSeed: number,
  onExit: () => void,
  /**
   * Something landed in the bag. The dungeon runs over the farm scene and cannot
   * reach its FarmCtx, but the farm's hotbar reads a CACHED bag — so without this
   * hook a player climbed out of a full run to a hotbar still showing the counts
   * he went down with. The farm passes `refreshBag`.
   */
  onLoot?: () => void,
): ViewTeardown {
  // Underground takes over the music from the farm above, and hands it straight
  // back on the way out (see the teardown).
  setAudioOverlay('dungeon');
  // The skeleton sprite is built with Texture.from below, which in Pixi 8 returns
  // an EMPTY texture for a URL that was never fetched — so the mobs down here were
  // invisible. Load it up front; by the time the first one spawns (out of sight of
  // the player, several tiles away) it is in the cache.
  void Assets.load(SKELETON_SPRITE).catch(() => { /* art missing: mob stays blank */ });
  clear(root);
  let alive = true;
  let depth = 1;
  let cfg: DungeonConfig | null = null;

  const wrap = el('div', 'relative w-full h-full overflow-hidden', { style: 'background:#0a0710' });
  const stageHost = el('div', 'absolute inset-0 select-none', { style: 'touch-action:none;background:#0a0710' });
  const topBar = el('div', 'absolute top-3 left-1/2 -translate-x-1/2 px-4 py-1.5 rounded-lg bg-black/60 text-white font-mono text-sm pointer-events-none', {}, 'Depth 1');
  const hpBar = el('div', 'absolute top-3 left-3 w-40');
  // Running haul for THIS descent. Nothing underground shows the bag, so before
  // this a whole run — every vein, every skeleton — produced no visible change
  // anywhere on screen and read as "mining down here does nothing".
  const haulBar = el('div', 'absolute top-3 left-3 mt-12 max-w-[45vw] text-[12px] text-amber-100/90 font-mono pointer-events-none');
  const haul = new Map<string, number>();
  const addHaul = (label: string, n: number) => {
    if (n <= 0) return;
    haul.set(label, (haul.get(label) ?? 0) + n);
    clear(haulBar);
    haulBar.append(el('div', 'text-[10px] uppercase tracking-wide text-white/50 mb-0.5', {}, 'Haul this run'));
    for (const [k, v] of haul)
      haulBar.append(el('div', 'inline-flex items-center gap-1 mr-2', {}, ...richText(`${v} ${k}`)));
    // The farm's cached bag is now stale — tell it, so climbing out shows the haul.
    onLoot?.();
  };

  const hint = el('div', 'absolute bottom-3 left-1/2 -translate-x-1/2 text-[12px] text-white/70 font-mono pointer-events-none', {}, 'Mine veins · fight skeletons · reach the down-stairs · climb the up-stairs to leave');
  const exitBtn = el('button', 'absolute top-3 right-3 z-10 px-2.5 py-1 rounded bg-black/60 text-white text-sm hover:bg-black/80', { onClick: () => leave() }, 'Leave');
  wrap.append(stageHost, topBar, hpBar, haulBar, hint, exitBtn);
  root.append(wrap);

  let app: Application | null = null;
  let cam: Camera | null = null;
  let player: PlayerHandle | null = null;
  let fog: Fog | null = null;
  let cave: Cave = genCave(entranceSeed ^ 0x1234);
  let worldLayer: Container | null = null;
  let entityLayer: Container | null = null;
  const skeletons: Skeleton[] = [];
  let skeletonSeq = 0;
  const veins: Vein[] = [];
  const keys = new Set<string>();
  let hp = 100, maxHp = 100, dead = false;
  let spawnAcc = 0;
  let armed = false;   // stairs only fire once you've stepped off the spawn/stairs tile
  let onKeyDown: ((e: KeyboardEvent) => void) | null = null;
  let onKeyUp: ((e: KeyboardEvent) => void) | null = null;

  const blockedTile = (tx: number, ty: number) => tx < 0 || ty < 0 || tx >= COLS || ty >= ROWS || cave.wall[idx(tx, ty)] === 1;
  const blockedPx = (wx: number, wy: number) => blockedTile(Math.floor(wx / TILE), Math.floor(wy / TILE));
  const clampPos = (x: number, y: number): [number, number] => [
    Math.max(TILE, Math.min((COLS - 1) * TILE, x)),
    Math.max(TILE, Math.min((ROWS - 1) * TILE, y)),
  ];

  function paintHp() {
    clear(hpBar);
    const pct = Math.max(0, Math.round((hp / maxHp) * 100));
    hpBar.append(
      el('div', 'text-[11px] text-white/80 font-mono mb-0.5 inline-flex items-center gap-1', {}, ...richText(`❤ ${Math.max(0, Math.round(hp))}/${maxHp}`)),
      el('div', 'h-2 rounded-full bg-black/50 overflow-hidden border border-white/20', {},
        el('div', 'h-full', { style: `width:${pct}%;background:${pct < 30 ? '#e0533a' : '#5ac36b'}` })));
  }

  // ─── Cave rendering ─────────────────────────────────────────────────────────
  function buildScene() {
    if (!app || !cam) return;
    worldLayer?.destroy({ children: true });
    entityLayer?.destroy({ children: true });
    skeletons.length = 0; veins.length = 0;

    worldLayer = new Container();
    // Floor + walls as flat blocky tiles (dark cave palette). One Graphics keeps it cheap.
    const g = new Graphics();
    for (let y = 0; y < ROWS; y++)
      for (let x = 0; x < COLS; x++) {
        const w = cave.wall[idx(x, y)];
        g.rect(x * TILE, y * TILE, TILE, TILE).fill({ color: w ? 0x241c30 : 0x3a3040 });
        if (!w) g.rect(x * TILE + 1, y * TILE + 1, TILE - 2, TILE - 2).stroke({ color: 0x2c2438, width: 1 });
      }
    worldLayer.addChild(g);
    cam.world.addChild(worldLayer);

    entityLayer = new Container();
    entityLayer.sortableChildren = true;
    cam.world.addChild(entityLayer);

    // Stairs markers.
    const stair = (tx: number, ty: number, label: string, color: number) => {
      const m = new Container();
      const ring = new Graphics().circle(0, 0, TILE * 0.42).fill({ color, alpha: 0.85 }).stroke({ color: 0xffffff, width: 2, alpha: 0.6 });
      m.addChild(ring);
      void label;
      m.x = (tx + 0.5) * TILE; m.y = (ty + 0.5) * TILE; m.zIndex = ty * TILE - 1;
      entityLayer!.addChild(m);
      const lbl = new Graphics();   // simple chevron
      lbl.moveTo(-6, -3).lineTo(0, 5).lineTo(6, -3).stroke({ color: 0xffffff, width: 2 });
      m.addChild(lbl);
      return m;
    };
    stair(cave.down[0], cave.down[1], 'down', 0x8a5cff);
    stair(cave.up[0], cave.up[1], 'up', 0x3a86ff);

    // Ore veins on random floor tiles — count + gem-richness rise with depth.
    const rnd = mulberry32((entranceSeed ^ 0x9e37) + depth * 101);
    const dcfg = cfg?.byDepth[String(depth)];
    const veinCount = 5 + depth * 2;
    const pool = cave.floors.filter((f) => {
      const x = f % COLS, y = (f / COLS) | 0;
      return Math.hypot(x - cave.spawn[0], y - cave.spawn[1]) > 3;
    });
    for (let i = 0; i < veinCount && pool.length; i++) {
      const pick = pool.splice(Math.floor(rnd() * pool.length), 1)[0];
      const tx = pick % COLS, ty = (pick / COLS) | 0;
      const gem = rnd() < (dcfg?.gemChance ?? 0.2);
      const s = new Sprite(Texture.from(gem ? '/sprites/mining/rock_gem.png' : '/sprites/mining/rock_ore.png'));
      s.anchor.set(0.5, 0.9); s.scale.set(TILE / 16 * 1.2);
      s.x = (tx + 0.5) * TILE; s.y = (ty + 0.9) * TILE; s.zIndex = ty * TILE;
      s.eventMode = 'static'; s.cursor = 'pointer';
      const vein: Vein = { sprite: s, tx, ty, gem, hits: cfg?.hitsPerVein ?? 6 };
      s.on('pointertap', (e) => { e.stopPropagation(); mineVein(vein); });
      veins.push(vein);
      entityLayer.addChild(s);
    }
  }

  function spawnSkeleton() {
    if (!entityLayer || !player) return;
    // On a floor tile out of sight of the player.
    const pool = cave.floors.filter((f) => Math.hypot((f % COLS) * TILE - player!.x, ((f / COLS) | 0) * TILE - player!.y) > TILE * 8);
    if (!pool.length) return;
    const pick = pool[Math.floor(Math.random() * pool.length)];
    const s = new Sprite(Texture.from(SKELETON_SPRITE));
    s.anchor.set(0.5, 0.9); s.scale.set(TILE / 16);
    const hpScale = cfg?.byDepth[String(depth)]?.mobHpScale ?? 1;
    const sk: Skeleton = {
      id: `sk-${++skeletonSeq}`, sprite: s, dying: false,
      x: (pick % COLS + 0.5) * TILE, y: ((pick / COLS | 0) + 0.5) * TILE, hp: 20 * hpScale, hurtCd: 0,
    };
    s.x = sk.x; s.y = sk.y; s.zIndex = sk.y;
    s.eventMode = 'static'; s.cursor = 'crosshair';
    s.on('pointertap', (e) => { e.stopPropagation(); strike(sk); });
    skeletons.push(sk);
    entityLayer.addChild(s);
  }

  function strike(sk: Skeleton) {
    if (!player || dead || sk.dying) return;
    // Say WHY nothing happened. A silent return on an out-of-reach click reads as
    // a broken skeleton, and the click already moved nothing (the sprite swallows
    // the tap), so the player has no feedback at all without this.
    // Out of reach is a state, not an event: mashing attack from across the room
    // used to narrate every swing.
    if (!withinAttackReach(Math.hypot(sk.x - player.x, sk.y - player.y))) return;
    // A skeleton is fought with the blade, not the axe: 'attack' is the sword row
    // (the swing frames carry the sword overlay).
    void player.playAction('attack', faceDir(sk.x - player.x, sk.y - (player.y - FOOT_OFFSET)));
    sk.hp -= 12;
    sk.sprite.tint = 0xff8080;
    setTimeout(() => { sk.sprite.tint = 0xffffff; }, 120);
    if (sk.hp <= 0) killSkeleton(sk);
  }

  /**
   * A skeleton went down. The server owns the reward — loot roll, XP, kill counter
   * — exactly like a field mob; before this the client just spliced the array and
   * destroyed the sprite, so every dungeon fight was unpaid.
   *
   * `dying` is set FIRST so the ticker stops chasing/biting with a corpse while the
   * request is in flight, and the sprite only leaves once the server has answered
   * — a dropped/failed kill must not silently delete a live skeleton.
   */
  function killSkeleton(sk: Skeleton) {
    if (sk.dying) return;
    sk.dying = true;
    sk.sprite.eventMode = 'none';
    const remove = () => {
      const i = skeletons.indexOf(sk);
      if (i >= 0) skeletons.splice(i, 1);
      sk.sprite.destroy();
    };
    void dispatchAction('dungeon.kill', (id) => dungeonApi.kill(depth, 'skeleton', id), { key: sk.id })
      .then((r) => {
        remove();
        for (const d of r.dropped) addHaul(d.name, d.quantity);
        toast(r.dropped.length
          ? `${r.dropped.map((d) => `${d.quantity} ${d.name}`).join(' · ')}${r.leveledUp ? ' · LEVEL UP!' : ''}`
          : `Skeleton down${r.leveledUp ? ' · LEVEL UP!' : ''}`);
        void ctx.refreshHud();
      })
      .catch((e) => {
        remove();                                  // it is dead on screen either way
        if (!isDropped(e)) toast(e instanceof Error ? e.message : 'The bones crumbled to nothing', 'error');
      });
  }

  function mineVein(vein: Vein) {
    if (!player || dead) return;
    if (Math.hypot((vein.tx + 0.5) * TILE - player.x, (vein.ty + 0.9) * TILE - player.y) > ATTACK_REACH)
      return toast('Too far — get closer');       // see strike(): never refuse in silence
    void guard('dungeon.mine', async () => {
      // Ore comes out with the PICKAXE ('mine'), not the axe.
      await player!.playAction('mine', faceDir((vein.tx + 0.5) * TILE - player!.x, (vein.ty + 0.9) * TILE - (player!.y - FOOT_OFFSET)));
      const r = await dungeonApi.mine(depth);
      toast(`+${r.oreDropped} ore${r.gemDropped ? ` · +${r.gemDropped} gem 💎` : ''}${r.leveledUp ? ' · LEVEL UP!' : ''}`);
      addHaul('ore', r.oreDropped);
      addHaul('gem', r.gemDropped);
      void ctx.refreshHud();
      const j = veins.indexOf(vein);
      if (j >= 0) veins.splice(j, 1);
      vein.sprite.destroy();
    });
  }

  async function guard(type: string, fn: () => Promise<void>) {
    try { await dispatchAction(type, fn, {}); }
    catch (e) { if (!isDropped(e)) toast(e instanceof Error ? e.message : 'Failed', 'error'); }
  }

  // ─── Depth transitions ───────────────────────────────────────────────────────
  function tileOfPlayer(): [number, number] {
    return [Math.floor(player!.x / TILE), Math.floor((player!.y - FOOT_OFFSET) / TILE)];
  }
  function onStairs(): 'down' | 'up' | null {
    if (!player) return null;
    const [px, py] = tileOfPlayer();
    if (Math.abs(px - cave.down[0]) <= 1 && Math.abs(py - cave.down[1]) <= 1) return 'down';
    if (Math.abs(px - cave.up[0]) <= 1 && Math.abs(py - cave.up[1]) <= 1 && depth === 1) return 'up';
    if (Math.abs(px - cave.up[0]) <= 1 && Math.abs(py - cave.up[1]) <= 1 && depth > 1) return 'up';
    return null;
  }

  function regen(newDepth: number) {
    depth = newDepth;
    armed = false;   // you arrive standing on the stairs again
    cave = genCave((entranceSeed ^ 0x1234) + depth * 7919);
    topBar.textContent = `Depth ${depth}`;
    buildScene();
    // Fresh fog per level (dark again); place the player at the spawn.
    fog?.destroy();
    fog = createFog(`dungeon-${entranceSeed}-${depth}`);
    if (player && cam) {
      player.x = (cave.spawn[0] + 0.5) * TILE; player.y = (cave.spawn[1] + 0.5) * TILE;
      cam.centerOn(player.x, player.y);
      if (player.sprite.parent !== entityLayer) entityLayer!.addChild(player.sprite);
    }
  }

  function leave() {
    if (!alive) return;
    alive = false;
    onExit();
  }

  // ─── Boot ────────────────────────────────────────────────────────────────────
  (async () => {
    cfg = await dungeonApi.config().catch(() => null);
    const h = await fieldApi.health().catch(() => null);
    if (h) { hp = h.hp; maxHp = h.maxHp; }
    paintHp();
    if (!alive) return;

    app = new Application();
    await app.init({ width: wrap.clientWidth || 820, height: wrap.clientHeight || 520, background: '#0a0710', antialias: false, preference: 'webgl' });
    if (!alive) { app.destroy(true); return; }
    stageHost.appendChild(app.canvas);
    cam = attachCamera(app, COLS * TILE, ROWS * TILE);
    fog = createFog(`dungeon-${entranceSeed}-${depth}`);

    buildScene();
    const look = loadAppearance(ctx.user.email || ctx.user.username || 'p') ?? defaultAppearance();
    player = await createPlayer(look, (cave.spawn[0] + 0.5) * TILE, (cave.spawn[1] + 0.5) * TILE);
    if (!alive) { player.destroy(); return; }
    entityLayer!.addChild(player.sprite);
    cam.centerOn(player.x, player.y);

    app.stage.on('pointertap', (e) => {
      if (!cam || !player || cam.dragged) return;
      const sc = cam.getScale();
      const wx = (e.global.x - cam.world.x) / sc, wy = (e.global.y - cam.world.y) / sc;
      player.setTarget(wx, wy + FOOT_OFFSET);
    });

    // WASD / arrows move; same physical-key handling as the farm (layout-agnostic).
    const pushKeys = () => {
      let vx = 0, vy = 0;
      if (keys.has('KeyA') || keys.has('ArrowLeft')) vx -= 1;
      if (keys.has('KeyD') || keys.has('ArrowRight')) vx += 1;
      if (keys.has('KeyW') || keys.has('ArrowUp')) vy -= 1;
      if (keys.has('KeyS') || keys.has('ArrowDown')) vy += 1;
      player?.setKeys(vx, vy);
    };
    onKeyDown = (e: KeyboardEvent) => { if (MOVE_CODES.has(e.code)) { e.preventDefault(); if (!keys.has(e.code)) { keys.add(e.code); pushKeys(); } } };
    onKeyUp = (e: KeyboardEvent) => { if (keys.delete(e.code)) pushKeys(); };
    window.addEventListener('keydown', onKeyDown);
    window.addEventListener('keyup', onKeyUp);

    app.ticker.add((t) => {
      if (!alive || !player || !cam) return;
      const dt = t.deltaMS / 1000;
      player.update(dt, clampPos, blockedPx);
      cam.follow(player.x, player.y, dt);

      // Skeletons: chase, and gnaw the player on contact.
      spawnAcc += t.deltaMS;
      const target = Math.min(3 + depth, 8);
      if (spawnAcc > 2500 && skeletons.length < target) { spawnAcc = 0; spawnSkeleton(); }
      for (const sk of skeletons) {
        if (sk.dying) continue;                    // a corpse mid-kill neither chases nor bites
        const dx = player.x - sk.x, dy = player.y - sk.y, d = Math.hypot(dx, dy) || 1;
        if (d > CONTACT) {
          const spd = TILE * 2.2 * dt;
          const nx = sk.x + (dx / d) * spd, ny = sk.y + (dy / d) * spd;
          if (!blockedPx(nx, ny)) { sk.x = nx; sk.y = ny; }
          sk.sprite.x = sk.x; sk.sprite.y = sk.y; sk.sprite.zIndex = sk.y;
          sk.sprite.scale.x = dx < 0 ? -(TILE / 16) : (TILE / 16);
        }
        sk.hurtCd -= t.deltaMS;
        if (d <= CONTACT && sk.hurtCd <= 0 && !dead) {
          sk.hurtCd = DMG_COOLDOWN_MS;
          const dmg = Math.round(4 * (cfg?.byDepth[String(depth)]?.mobHpScale ?? 1));
          hurt(dmg);
        }
      }

      // Fog (dark cave — the light travels with the hero).
      if (fog) {
        if (fog.layer.parent !== cam.world) cam.world.addChild(fog.layer);
        cam.world.setChildIndex(fog.layer, cam.world.children.length - 1);
        const fsc = cam.getScale(), sw = app!.screen.width, sh = app!.screen.height;
        fog.update(player.x / TILE, player.y / TILE, {
          minTx: Math.floor((-cam.world.x) / (TILE * fsc)),
          minTy: Math.floor((-cam.world.y) / (TILE * fsc)),
          maxTx: Math.ceil((sw - cam.world.x) / (TILE * fsc)),
          maxTy: Math.ceil((sh - cam.world.y) / (TILE * fsc)),
        });
      }

      // Stairs transition on arrival — but only after you've stepped off the tile
      // you spawned on (the up-stairs sit at the spawn), so you don't instantly
      // bounce back out or ping-pong between levels.
      const st = onStairs();
      if (!st) armed = true;
      if (armed && st === 'down' && depth < (cfg?.depths ?? 3)) { toast(`Descending to depth ${depth + 1}…`); regen(depth + 1); }
      else if (armed && st === 'up') { if (depth > 1) { toast(`Climbing to depth ${depth - 1}…`); regen(depth - 1); } else leave(); }
    });
  })().catch((err) => {
    stageHost.appendChild(el('div', 'p-4 text-sm text-error font-mono', {}, `Dungeon failed: ${err instanceof Error ? err.message : String(err)}`));
  });

  function hurt(dmg: number) {
    hp = Math.max(0, hp - dmg);
    paintHp();
    void dispatchAction('field.damage', (id) => fieldApi.damage(dmg, id), { noDebounce: true })
      .then(() => void ctx.refreshHud()).catch(() => undefined);
    if (hp <= 0 && !dead) {
      dead = true;
      void player?.playDeath();
      toast('You fell in the dark… hauled back to the surface', 'error');
      setTimeout(() => leave(), 1400);
    }
  }

  return () => {
    setAudioOverlay(null);
    alive = false;
    if (onKeyDown) window.removeEventListener('keydown', onKeyDown);
    if (onKeyUp) window.removeEventListener('keyup', onKeyUp);
    fog?.destroy();
    player?.destroy();
    cam?.destroy();
    app?.destroy(true);
    wrap.remove();
  };
}
